import React, { useEffect, useRef } from "react";

class OrbitLoader {
  constructor() {
    this.injectCSS();
  }

  injectCSS() {
    const existingStyle = document.getElementById("orbit-loader-simple-styles");
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = "orbit-loader-simple-styles";
    style.textContent = `
      .orbit-simple-container {
        position: relative !important;
        margin: 0 auto !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
      }
      
      .orbit-simple-container.size-small { 
        width: 30px !important; 
        height: 30px !important; 
      }
      
      .orbit-simple-container.size-medium { 
        width: 50px !important; 
        height: 50px !important; 
      }
      
      .orbit-simple-container.size-large { 
        width: 80px !important; 
        height: 80px !important; 
      }
      
      .orbit-simple-container.size-xl { 
        width: 100px !important; 
        height: 100px !important; 
      }
      
      .orbit-simple-path {
        position: absolute !important;
        width: 100% !important;
        height: 100% !important;
        border: 2px solid #1e3a8a !important;
        border-radius: 50% !important;
        box-shadow: 0 0 10px rgba(30, 58, 138, 0.4) !important;
        background: transparent !important;
        top: 0 !important;
        left: 0 !important;
      }
      
      .orbit-simple-planet {
        position: absolute !important;
        border-radius: 50% !important;
        z-index: 10 !important;
        will-change: transform !important;
      }
      
      .orbit-simple-container.size-small .orbit-simple-planet { 
        width: 6px !important; 
        height: 6px !important; 
      }
      
      .orbit-simple-container.size-medium .orbit-simple-planet { 
        width: 10px !important; 
        height: 10px !important; 
      }
      
      .orbit-simple-container.size-large .orbit-simple-planet { 
        width: 16px !important; 
        height: 16px !important; 
      }
      
      .orbit-simple-container.size-xl .orbit-simple-planet { 
        width: 20px !important; 
        height: 20px !important; 
      }
      
      .orbit-planet-red-js {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
        box-shadow: 0 0 12px rgba(239, 68, 68, 0.6) !important;
      }
      
      .orbit-planet-yellow-js {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        box-shadow: 0 0 12px rgba(245, 158, 11, 0.6) !important;
      }
      
      .orbit-planet-blue-js {
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
        box-shadow: 0 0 12px rgba(59, 130, 246, 0.6) !important;
      }
      
      .orbit-simple-path.white-path {
        border-color: rgba(100, 100, 100, 0.8) !important;
        box-shadow: 0 0 10px rgba(100, 100, 100, 0.4) !important;
      }
      
      /* LoadingButton overrides */
      .loading-btn .orbit-simple-container {
        margin: 0 !important;
        width: 18px !important;
        height: 18px !important;
      }
      
      .loading-btn .orbit-simple-planet {
        width: 4px !important;
        height: 4px !important;
      }
    `;

    document.head.appendChild(style);
  }
}

// React Component with JavaScript Animation
export const OrbitLoaderComponent = ({
  size = "small",
  showPath = false,
  className = "",
  style = {},
  color = "gray",
}) => {
  const containerRef = useRef(null);
  const redPlanetRef = useRef(null);
  const yellowPlanetRef = useRef(null);
  const bluePlanetRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    new OrbitLoader();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const redPlanet = redPlanetRef.current;
    const yellowPlanet = yellowPlanetRef.current;
    const bluePlanet = bluePlanetRef.current;

    if (!container || !redPlanet || !yellowPlanet || !bluePlanet) return;

    // Boyuta göre radius hesaplama
    const sizeMap = {
      small: { radius: 12, center: 15 },
      medium: { radius: 20, center: 25 },
      large: { radius: 32, center: 40 },
      xl: { radius: 42, center: 50 },
    };

    const { radius, center } = sizeMap[size] || sizeMap.small;

    let startTime = Date.now();
    const duration = 2000; // 2 saniye

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % duration) / duration;
      const angle = progress * 2 * Math.PI;

      // Her gezegen için farklı başlangıç açısı
      const redAngle = angle;
      const yellowAngle = angle + (2 * Math.PI) / 3; // 120 derece fark
      const blueAngle = angle + (4 * Math.PI) / 3; // 240 derece fark

      // Pozisyon hesaplama
      const redX = center + radius * Math.cos(redAngle);
      const redY = center + radius * Math.sin(redAngle);

      const yellowX = center + radius * Math.cos(yellowAngle);
      const yellowY = center + radius * Math.sin(yellowAngle);

      const blueX = center + radius * Math.cos(blueAngle);
      const blueY = center + radius * Math.sin(blueAngle);

      // Planet boyutlarını merkez için ayarlama
      const planetSize = parseInt(getComputedStyle(redPlanet).width) / 2;

      redPlanet.style.transform = `translate(${redX - planetSize}px, ${
        redY - planetSize
      }px)`;
      yellowPlanet.style.transform = `translate(${yellowX - planetSize}px, ${
        yellowY - planetSize
      }px)`;
      bluePlanet.style.transform = `translate(${blueX - planetSize}px, ${
        blueY - planetSize
      }px)`;

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size]);

  const pathColorClass = color === "white" ? "white-path" : "";

  return (
    <div
      ref={containerRef}
      className={`orbit-simple-container size-${size} ${className}`}
      style={style}
    >
      {showPath && <div className={`orbit-simple-path ${pathColorClass}`} />}
      <div
        ref={redPlanetRef}
        className="orbit-simple-planet orbit-planet-red-js"
        style={{ top: 0, left: 0 }}
      />
      <div
        ref={yellowPlanetRef}
        className="orbit-simple-planet orbit-planet-yellow-js"
        style={{ top: 0, left: 0 }}
      />
      <div
        ref={bluePlanetRef}
        className="orbit-simple-planet orbit-planet-blue-js"
        style={{ top: 0, left: 0 }}
      />
    </div>
  );
};

export default OrbitLoader;
